import { useEffect, useMemo, useRef, useState } from 'react';
import { Funnel } from '@gravity-ui/icons';
import { Button, Icon, Popup, TextInput } from '@gravity-ui/uikit';
import type { MRT_Column } from 'material-react-table';

import styles from './styles.module.scss';
import { useGetEstablishmentsQuery } from 'store/api';
import { skipToken } from '@reduxjs/toolkit/query';

interface Person {
	id: number;
	email: string;
	role: string;
	firstName: string;
	middleName: string;
	lastName: string;
	establishment: {
		id?: number;
		name?: string | null;
	} | null;
	imageUrl: string;
	region: {
		code?: number;
		fullname: string;
		guid?: string;
		name?: string;
	};
	specialty: string;
	active: boolean;
	createdDate: string;
}

interface CustomEstablishmentFilterProps {
	column?: MRT_Column<Person>;

	onApplyFilters?: (establishments: string[]) => void;

	selectedFilters: string[];
}

export const CustomEstablishmentsFilter = ({
	column,
	selectedFilters,
	onApplyFilters,
}: CustomEstablishmentFilterProps) => {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef(null);

	const [searchValue, setSearchValue] = useState('');
	const [selectedEstablishments, setSelectedEstablishments] = useState<string[]>(
		(column?.getFilterValue() as string[]) || [],
	);

	const { data: estList } = useGetEstablishmentsQuery(
		{ pageNumber: 0, pageSize: 100, sort: 'DESC', sortField: 'id' } ?? skipToken,
	);

	useEffect(() => {
		setSelectedEstablishments(selectedFilters || []);
	}, [selectedFilters]);

	const allEstablishments = useMemo(() => {
		return Array.from(new Set(estList?.content.map((p) => p?.name?.trim()).filter((n): n is string => Boolean(n))));
	}, [estList?.content]);

	const filteredEstablishments = useMemo(() => {
		return allEstablishments.filter((name) => name.toLowerCase().includes(searchValue.toLowerCase()));
	}, [allEstablishments, searchValue]);

	const handleCheckboxChange = (estName: string) => {
		setSelectedEstablishments((selectedEstablishments) =>
			selectedEstablishments.includes(estName)
				? selectedEstablishments.filter((v) => v !== estName)
				: [...selectedEstablishments, estName],
		);
	};

	const handleApply = () => {
		if (column) {
			column.setFilterValue(selectedEstablishments);
		}

		if (onApplyFilters) {
			onApplyFilters(selectedEstablishments);
		}
		setOpen(false);
	};

	return (
		<>
			{column ? (
				<Button
					ref={buttonRef}
					onClick={() => setOpen((prevOpen) => !prevOpen)}
					className={styles.popup_btn}
					view="flat-contrast"
					size="m"
				>
					<Icon data={Funnel} size={18} />
				</Button>
			) : (
				<Button ref={buttonRef} onClick={() => setOpen((prevOpen) => !prevOpen)} size="m" view="flat">
					<Icon data={Funnel} size={18} />
					Учреждение
				</Button>
			)}

			<Popup open={open} anchorRef={buttonRef} placement="bottom">
				<div className={styles.popup_container}>
					<div className={styles.popup_container__input}>
						<TextInput
							type="text"
							placeholder="Поиск учреждений..."
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</div>

					<div className={styles.popup_container__list}>
						{filteredEstablishments.map((estName) => {
							const checked = selectedEstablishments.includes(estName);
							return (
								<div key={estName} className={styles.popup_container__list__establishment}>
									<input
										type="checkbox"
										checked={checked}
										onChange={() => handleCheckboxChange(estName)}
										className={styles.popup_container__list__establishment__checkbox}
									/>
									<span>{estName}</span>
								</div>
							);
						})}
					</div>

					<div className={styles.popup_container__btn}>
						<Button onClick={handleApply} size="l" className={styles.popup_container__btn__apply}>
							Применить
						</Button>
					</div>
				</div>
			</Popup>
		</>
	);
};
